import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';

import {
  EngagementsDataService,
  SuggestionsDataService,
  TellentAssistantControlService,
} from '@core/tellent-assistant-logic';
import {
  bannerSlotKey,
  type BannerSlot,
  type BannerSuggestion,
} from '@core/tellent-assistant-types';
import { TellentAssistantSuggestionBannerComponent } from '@core/tellent-assistant-ui';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentDevFlag } from '@core/tellent-types';

@Component({
  selector: 'tellent-assistant-suggestion-banner',
  imports: [TellentAssistantSuggestionBannerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible(); as item) {
      <tellent-assistant-suggestion-banner-view
        [suggestion]="item"
        [size]="size()"
        (bannerClick)="onClick(item)"
      />
    }
  `,
  host: {
    '[style.display]': "visible() ? 'block' : 'none'",
  },
})
export class TellentAssistantSuggestionBannerContainerComponent {
  private readonly suggestions = inject(SuggestionsDataService);
  private readonly engagements = inject(EngagementsDataService);
  private readonly control = inject(TellentAssistantControlService);
  private readonly assistantEnabled = injectDevFlagSignal(TellentDevFlag.TellentAssistant);
  private readonly slotKey = computed<string>(() => bannerSlotKey(this.slot()));
  private readonly dismissed = computed<boolean>(() => this.suggestions.isDismissed(this.slot())());
  private readonly top = computed<BannerSuggestion | null>(
    () => this.suggestions.bySlot(this.slot())()?.suggestions[0] ?? null,
  );

  private readonly loadSuggestionsWhenEnabled = effect(() => {
    const enabled = this.assistantEnabled();
    this.slotKey();
    if (!enabled) return;
    this.suggestions.loadSuggestions(untracked(() => this.slot())).subscribe();
  });

  protected readonly visible = computed<BannerSuggestion | null>(() =>
    this.assistantEnabled() && !this.dismissed() ? this.top() : null,
  );

  public readonly slot = input.required<BannerSlot>();
  public readonly size = input<'default' | 'compact'>('default');

  protected onClick(item: BannerSuggestion): void {
    this.control.openWith(item);
    this.engagements.recordEngagement(item.id);
    this.suggestions.dismiss(this.slot());
  }
}
